<template>
  <div
    id="vuecustomizer"
    :class="bemBlock"
    :data-get-content-asset="getContentAssetUrl"
  >
    <BaseBackButton />
    <div class="product-detail" :data-pid="productId">
      <meta itemprop="brand" content="BIMBA Y LOLA" />

      <div
        :class="
          bemElement(
            isBreakpointDesktop ? 'images-container' : 'carousel-container'
          )
        "
      >
        <div v-if="!hasImages" :class="bemElement('pre-placeholder')">
          <span :class="[bemElement('pic'), 'img']" />
        </div>
        <BaseImage
          v-for="(imageSet, imageIndex) in productImageSets"
          :key="imageIndex"
          :class="bemElement('pic')"
          :image="getDefaultImageFromProductImageSet(imageSet)"
          :loading="imageIndex < 1 || areCriticalImagesLoaded ? 'eager' : null"
          :placeholder="getPlaceholderImageFromProductImageSet(imageSet)"
          :sizes="imageAttributeSizes.PDP_GALLERY"
          :srcset="getSrcsetFromProductImageSet(imageSet)"
          @image-click="(event) => handleProductImageClick(event, imageIndex)"
          @load="handleImageLoad(imageIndex)"
        />
        <BaseCarousel
          v-if="$windowWidth < 575 && hasImages"
          :class="bemElement('carousel')"
          :items="productImageSets"
          @carousel-click="
            (event, currentIndex) =>
              handleProductImageClick(event, currentIndex)
          "
        >
          <template #default="slotProps">
            <BaseImage
              :ref="`carouselItem${slotProps.index}`"
              :class="slotProps.classname"
              :image="getDefaultImageFromProductImageSet(slotProps.item)"
              :image-classes="bemElement('image')"
              :loading="
                slotProps.index === 0 || areCriticalImagesLoaded
                  ? 'eager'
                  : null
              "
              :placeholder="
                getPlaceholderImageFromProductImageSet(slotProps.item)
              "
              :sizes="imageAttributeSizes.PDP_GALLERY"
              :srcset="getSrcsetFromProductImageSet(slotProps.item)"
              @load="handleImageLoad(slotProps.index)"
            />
          </template>
        </BaseCarousel>
      </div>
      <div :class="bemElement('text-container')">
        <div :class="bemElement('sticky-container')">
          <div
            class="interaction--loading-content"
            :class="{ 'interaction-active': !loading }"
            data-interaction="interaction--loading-content"
          >
            <div class="loading-content">
              <BaseSpinner small grey />
            </div>
            <div class="loaded-content">
              <div :class="bemElement('sticky-child-container')">
                <h1 :class="bemElement('title')">
                  {{ name }}
                </h1>
                <BasePrice
                  v-if="mainProductUnitPrice"
                  bem="the-product-details-price"
                  :product="mainProductUnitPrice"
                  :title="$msg('price.from', 'customizer')"
                />
              </div>
              <p :class="bemElement('description')">
                {{ product.longDescription }}
              </p>
              <p v-if="mainProductUnitPrice" :class="bemElement('unit-price')">
                <BasePrice
                  bem="the-product-details-unit-price"
                  :product="mainProductUnitPrice"
                  :title="
                    productId && productId === 'designer'
                      ? $msg('designer.main.unit.price', 'customizer')
                      : $msg('customizer.main.unit.price', 'customizer')
                  "
                />
              </p>
              <p
                v-if="complementProductUnitPrice"
                :class="bemElement('unit-price')"
              >
                <BasePrice
                  bem="the-product-details-unit-price"
                  :product="complementProductUnitPrice"
                  :title="$msg('product.complement.unit.price', 'customizer')"
                />
                {{ $msg('product.unit', 'customizer') }}
              </p>
              <button
                v-if="isBreakpointMobile && !controlsVisible"
                :class="[
                  bemElement('customize-btn', { 'full-width': isAppRequest }),
                  'btn',
                  'btn--cta',
                ]"
                @click="goToCustomizer"
              >
                <span>{{ $msg('button.customize', 'customizer') }}</span>
              </button>
              <button
                v-if="isBreakpointDesktop"
                :class="[bemElement('customize-btn'), 'btn', 'btn-primary']"
                @click="goToCustomizer"
              >
                <span>{{ $msg('button.customize', 'customizer') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      ref="controls"
      v-observe-visibility="handleMobileButtons"
      :class="bemElement('controls')"
    >
      <TheProductCustomizerVisualizer :class="bemElement('visualizer')" />
      <TheProductCustomizerSelector
        :bem="'the-product-details'"
        :class="bemElement('selector')"
        :cta-visible="controlsVisible"
      />
    </div>
    <div :class="bemElement('get-inspired')">
      <BaseTitleSubtitle
        :class="bemElement('title-subtitle')"
        :title="$msg('getinspired.title', 'customizer')"
        :subtitle="name"
      />
      <BaseCarousel
        :class="bemElement('slider')"
        :are-controls-shown="false"
        :items="imageSliderImages"
        :swiper-options="{
          allowTouchMove: true,
          breakpoints: {
            768: {
              slidesPerView: 3,
            },
            1025: {
              slidesPerView: 4,
              spaceBetween: 4,
            },
          },
          loop: false,
          slidesPerView: 2.5,
          spaceBetween: 2,
        }"
      />
    </div>
    <TheProductDetailsZoom
      :class="{
        'is-visible': isZoomOpen,
      }"
      :is-visible="isZoomOpen"
      :images="productImageSets"
      :loading="areGalleryImagesLoaded ? 'eager' : null"
      :zoom-requirements="zoomRequirements"
      @close-click="closeZoom(true)"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import EventBusConfig from '../../config/eventBusConfig';
import bemClassesMixin from '../../mixins/bemClassesMixin';
import pageDataMixin from '../../mixins/pageDataMixin';
import { productBaseMixin } from '../../mixins/productPropBaseMixin';
import { productImageSetMixin } from '../../mixins/productImageSetMixin';
import sfraProductMixin from '../../mixins/sfraProductMixin';
import windowResizeMixin from '../../mixins/windowResizeMixin';
import BaseBackButton from '../BaseBackButton.vue';
import BaseCarousel from '../BaseCarousel.vue';
import BaseImage from '../BaseImage.vue';
import BasePrice from '../BasePrice.vue';
import BaseSpinner from '../BaseSpinner.vue';
import BaseTitleSubtitle from '../BaseTitleSubtitle.vue';
import TheProductCustomizerSelector from './TheProductCustomizerSelector.vue';
import TheProductCustomizerVisualizer from './TheProductCustomizerVisualizer.vue';
import TheProductDetailsZoom from '../TheProductDetailsZoom.vue';
import { getters } from '../../store';
import getFrontConfig from '../../../common/getFrontConfig';
import {
  getParameterValueFromRequest,
  isWebPlatform,
} from '../../../common/utils/api';
import { PAGE_TYPES } from '../../../../../../config/analytics';

const {
  apiConfig,
  customizerConfig,
  productImages: { imageSets },
  responsive: { bp_575: bp575 },
  urls: { customizerResources, getContentAssetUrl },
} = getFrontConfig();

/*
Resolutions - PDP img size

1 foto (< 1024)
375 x ...: 375x563 (1x), 750x1126 (2x)
768 x ...: 512x768 (1x), 1024x1536 (2x)

2 fotos contiguas (>= 1024)
1024 x ...: 385x578 (1x), 770x1156 (2x)
1440 x 900: 543x815 (1x), 1086x1630 (2x)
2560 x 1080: 944x1415

Zoom 100vw: 375, 768, 1024, 1440, 2560, 5120...

srcset:
480px,
640px,
768px,
1080px,
1280px,
1440px,
1920px
*/
const SIZES = {
  PDP_GALLERY: '(min-width: 1024px) 37.5vw, (min-width: 768px) 66.6vw, 100vw',
  PDP_ZOOM: '100vw',
};

export default {
  name: 'TheProductCustomizer',

  components: {
    BaseBackButton,
    BaseCarousel,
    BaseImage,
    BasePrice,
    BaseSpinner,
    BaseTitleSubtitle,
    TheProductCustomizerSelector,
    TheProductCustomizerVisualizer,
    TheProductDetailsZoom,
  },

  mixins: [
    bemClassesMixin,
    pageDataMixin,
    productBaseMixin,
    productImageSetMixin,
    sfraProductMixin,
    windowResizeMixin,
  ],

  props: {
    bem: {
      type: [Array, String],
      default: 'the-product-details',
    },
  },

  data() {
    return {
      controlsVisible: false,
      dataChangeBreakpoint: bp575,
      getContentAssetUrl,
      imageAttributeSizes: SIZES,
      isZoomOpen: false,
      loadedImagesIndexes: [],
      zoomRequirements: {},
    };
  },

  computed: {
    ...mapGetters({
      mainProducts: getters.productCustomizer.mainProducts,
      complementProducts: getters.productCustomizer.complementProducts,
    }),
    ...mapState({
      loading: (state) => state.productCustomizer.loading,
      product: (state) => state.productCustomizer.productSet,
    }),
    /** @type {boolean} Critical images are those which should be loaded
     * first.
     * - Mobile: 1st image
     * - Desktop: 1st and 2nd images
     */
    areCriticalImagesLoaded() {
      if (this.isBreakpointMobile) {
        return this.loadedImagesIndexes.indexOf(0) > -1;
      }
      return (
        this.loadedImagesIndexes.indexOf(0) > -1 &&
        this.loadedImagesIndexes.indexOf(1) > -1
      );
    },
    areGalleryImagesLoaded() {
      return (
        this.loadedImagesIndexes.indexOf(this.productImageSets.length - 1) > -1
      );
    },
    complementProductUnitPrice() {
      const products = [];
      this.complementProducts.forEach((set) => {
        products.push(...set.individualProducts);
      });
      return this.getLowestPriceProduct(products);
    },
    hasImages() {
      return this.productImages && this.productImages['pdp-large']?.length > 1;
    },
    imageSliderImages() {
      const imageNames = customizerConfig?.[this.productId]?.inspired || [];
      return imageNames.reduce((acc, name) => {
        acc.push({
          url: `${customizerResources}${this.productId}/slider/${name}`,
        });
        return acc;
      }, []);
    },
    isAppRequest() {
      const platform = getParameterValueFromRequest(
        apiConfig.PLATFORM_PARAMETER_NAME
      );
      return !isWebPlatform(platform);
    },
    mainProductUnitPrice() {
      return this.getLowestPriceProduct(this.mainProducts);
    },
    productImageSets() {
      return this.getProductImageSets(imageSets.PDP);
    },
  },

  watch: {
    product() {
      this.pageTitle = this.product.productName;
    },
  },

  mounted() {
    this.pageType = PAGE_TYPES.product;
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.closeZoom);
  },

  methods: {
    closeZoom() {
      if (!this.isZoomOpen) return;
      this.isZoomOpen = false;
      this.$emit('zoomclose');
      this.$eventBus.$emit(EventBusConfig.events.TheProductDetails.zoomClosed);
    },
    getLowestPriceProduct(products) {
      if (products.length === 0) return null;
      return products.reduce((acc, product) => {
        const lowestPrice = this.getProductLowestPriceValue(acc);
        const price = this.getProductLowestPriceValue(product);
        return price < lowestPrice ? product : acc;
      }, products[0]);
    },
    getProductLowestPriceValue(product) {
      return product.price.sales
        ? product.price.sales.value
        : product.price.list.value;
    },
    getZoomPercentileY(event, index) {
      const clickedItem =
        this.$refs[`carouselItem${index}`]?.$el || event.currentTarget;
      const clickedElementTop = clickedItem?.getBoundingClientRect()
        ? clickedItem.getBoundingClientRect().top + window.scrollY
        : 0;
      const relativeTopToImage =
        event.pageY >= clickedElementTop ? event.pageY - clickedElementTop : 0;
      return relativeTopToImage / clickedItem?.offsetHeight;
    },
    goToCustomizer() {
      this.$refs.controls?.scrollIntoView({ behavior: 'smooth' });
    },
    handleMobileButtons(isVisible) {
      this.controlsVisible = isVisible;
    },
    handleImageLoad(index) {
      this.loadedImagesIndexes.push(+index);
    },
    handleProductImageClick(event, index = null) {
      this.openZoom(index, this.getZoomPercentileY(event, index));
    },
    openZoom(ordinal = 0, percentileY = 0) {
      if (this.isZoomOpen) return;
      this.zoomRequirements = {
        ordinal,
        percentileY,
      };
      this.isZoomOpen = true;
      this.$emit('zoomopen');
      this.$eventBus.$emit(
        EventBusConfig.events.TheProductDetails.zoomOpened,
        this.zoomRequirements
      );
      // trigger when any navigation event
      window.addEventListener('popstate', this.closeZoom, {
        once: true,
        passive: true,
      });
    },
  },
};
</script>
